import { mediaMax } from "components/designV2/breakpoints";
import { Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { toRgba } from "config/colors.rgba.logic";
import { TypographyV2 } from "config/font";
import styled from "styled-components";

export const MonthlyReportCard = styled.section`
  background-color: ${ColorV2.White};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
`;

export const MonthlyReportHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MonthlyReportTitle = styled.h2`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const MonthlyReportDescription = styled.p`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const MonthlyReportRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

export const MonthlyReportRowCard = styled.div`
  background-color: ${ColorV2.Blue7};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const MonthlyReportSummaryRow = styled.div`
  align-items: center;
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns:
    minmax(240px, 1.4fr) minmax(120px, 0.7fr) minmax(96px, 0.6fr)
    minmax(160px, 0.9fr) minmax(140px, 0.8fr) 32px;
  min-height: 72px;
  padding: ${Spacing.sm}px ${Spacing.md}px;

  ${mediaMax.tablet} {
    align-items: flex-start;
    grid-template-columns: minmax(0, 1fr) 32px;
  }
`;

export const MonthlyWorkerCell = styled.div`
  min-width: 0;
`;

export const MonthlyMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  ${mediaMax.tablet} {
    grid-column: 1 / -1;
  }
`;

export const MonthlyMetricValue = styled.div`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

export const MonthlyMetricLabel = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const MonthlyExpandButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${ColorV2.Blue1};
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;
`;

export const MonthlyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.sm}px ${Spacing.md}px ${Spacing.md}px;
`;

const detailGridColumns =
  "minmax(120px, 0.8fr) minmax(160px, 1fr) minmax(96px, 0.6fr) minmax(120px, 0.8fr) minmax(140px, 0.8fr)";

export const MonthlyDetailsHeader = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: ${detailGridColumns};
  padding: ${Spacing.sm}px 8px 0;

  ${mediaMax.tablet} {
    display: none;
  }
`;

export const MonthlyDetailsRow = styled.div`
  ${TypographyV2.b2}
  align-items: center;
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: ${detailGridColumns};
  min-height: 40px;
  padding: 0 8px;

  ${mediaMax.tablet} {
    align-items: flex-start;
    border-top: 1px solid ${ColorV2.Blue6};
    display: flex;
    flex-direction: column;
    gap: ${Spacing.xs}px;
    padding: ${Spacing.sm}px 0 0;
  }
`;

export const MonthlyStatusPill = styled.span<{
  $variant: "bank" | "swish" | "neutral";
}>`
  ${TypographyV2.b2}
  align-items: center;
  background-color: ${(props) =>
    props.$variant === "neutral"
      ? ColorV2.White
      : toRgba(ColorV2.PositiveGreen, 0.14)};
  border-radius: 999px;
  color: ${(props) =>
    props.$variant === "neutral" ? ColorV2.Blue4 : ColorV2.PositiveGreen};
  display: inline-flex;
  gap: 6px;
  min-height: 32px;
  padding: 0 ${Spacing.md}px;
  width: fit-content;
`;

export const MonthlyTotalRow = styled.div`
  ${TypographyV2.b1}
  align-items: center;
  color: ${ColorV2.Blue1};
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns:
    minmax(240px, 1.4fr) minmax(120px, 0.7fr) minmax(96px, 0.6fr)
    minmax(160px, 0.9fr) minmax(140px, 0.8fr) 32px;
  min-height: 56px;
  padding: ${Spacing.sm}px ${Spacing.md}px 0;

  ${mediaMax.tablet} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const MonthlyEmptyState = styled.div`
  ${TypographyV2.b2}
  align-items: center;
  color: ${ColorV2.Blue4};
  display: flex;
  justify-content: center;
  min-height: 220px;
`;
